import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, List, Tag, Space, Typography } from 'antd';
import { BellOutlined, WifiOutlined } from '@ant-design/icons';
import socketService from '../services/socket';
import { getAlertStatusIcon } from '../utils/alertDisplay';
import { toLocalTime } from '../utils/timezone';

const { Text } = Typography;
const MAX_EVENTS = 80;
const DISPLAY_LIMIT = 5;

function socketTargetMatches(modalTargetId, data) {
  const a = Number(modalTargetId);
  const b = Number(data?.targetId ?? data?.id);
  return Number.isFinite(a) && Number.isFinite(b) && a === b;
}

function eventKey(ev) {
  return `${ev.targetId}-${ev.timestamp}-${ev.status}-${ev.latency ?? ''}`;
}

function logToEvent(log, target) {
  if (!log || !target?.id) return null;
  if (log.target_id != null && Number(log.target_id) !== Number(target.id)) {
    return null;
  }
  return {
    id: log.id ? String(log.id) : eventKey({ targetId: target.id, timestamp: log.timestamp, status: log.status, latency: log.latency }),
    targetId: target.id,
    targetName: target.name,
    status: log.status,
    latency: log.latency,
    errorMessage: log.error_message || log.errorMessage,
    message: log.message,
    timestamp: log.timestamp,
    source: 'history',
  };
}

function mergeTargetEvents(logs, target, liveEvents) {
  const map = new Map();
  (logs || []).forEach((log) => {
    const ev = logToEvent(log, target);
    if (ev && ['UP', 'DOWN', 'TIMEOUT'].includes(ev.status)) {
      map.set(eventKey(ev), ev);
    }
  });
  liveEvents.forEach((ev) => {
    map.set(eventKey(ev), ev);
  });
  return Array.from(map.values())
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
    .slice(-MAX_EVENTS);
}

/** One row per status period — not every poll interval while status stays the same. */
function collapseStatusTransitions(events) {
  if (!events?.length) return [];

  const out = [];
  let prevStatus = null;

  for (const ev of events) {
    if (ev.status !== prevStatus) {
      out.push({ ...ev });
      prevStatus = ev.status;
      continue;
    }
    if (out.length) {
      out[out.length - 1] = {
        ...out[out.length - 1],
        ...ev,
        timestamp: ev.timestamp,
        latency: ev.latency ?? out[out.length - 1].latency,
        errorMessage: ev.errorMessage || out[out.length - 1].errorMessage,
        source: ev.source === 'live' ? 'live' : out[out.length - 1].source,
      };
    }
  }

  return out.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
}

function formatLatency(latency) {
  if (latency == null || latency === '') return '-';
  const n = Number(latency);
  return Number.isFinite(n) ? `${n} ms` : String(latency);
}

const TargetDetailAlerts = ({
  target,
  visible,
  logs = [],
}) => {
  const [liveEvents, setLiveEvents] = useState([]);
  const lastStatusRef = useRef(null);

  const upsertLiveEvent = useCallback((data, { statusChanged }) => {
    const ev = {
      id: eventKey(data),
      targetId: data.targetId,
      targetName: data.targetName || target?.name,
      status: data.status,
      latency: data.latency,
      errorMessage: data.errorMessage,
      message: data.message,
      timestamp: data.timestamp || new Date().toISOString(),
      host: data.host,
      port: data.port,
      source: 'live',
    };

    setLiveEvents((prev) => {
      if (!statusChanged && prev.length > 0) {
        const latest = prev[prev.length - 1];
        if (latest.status === ev.status) {
          const updated = {
            ...latest,
            timestamp: ev.timestamp,
            latency: ev.latency ?? latest.latency,
            errorMessage: ev.errorMessage || latest.errorMessage,
            source: 'live',
          };
          return [...prev.slice(0, -1), updated];
        }
      }

      const map = new Map(prev.map((e) => [eventKey(e), e]));
      map.set(eventKey(ev), ev);
      return Array.from(map.values())
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
        .slice(-MAX_EVENTS);
    });
  }, [target?.name]);

  const handleStatusDetail = useCallback((data) => {
    if (!socketTargetMatches(target?.id, data)) return;

    const previousStatus = data.previousStatus ?? lastStatusRef.current;
    const statusChanged = previousStatus != null && previousStatus !== data.status;
    lastStatusRef.current = data.status;

    upsertLiveEvent(
      { ...data, targetName: data.targetName || target.name },
      { statusChanged }
    );
  }, [target, upsertLiveEvent]);

  const handleTargetAlert = useCallback((data) => {
    if (!socketTargetMatches(target?.id, data)) return;

    const previousStatus = data.previousStatus ?? lastStatusRef.current;
    const statusChanged = previousStatus != null && previousStatus !== data.status;
    lastStatusRef.current = data.status;

    upsertLiveEvent(
      {
        ...data,
        targetName: data.targetName || target.name,
        latency: data.latency ?? null,
        timestamp: data.timestamp || new Date().toISOString(),
      },
      { statusChanged: statusChanged || Boolean(data.isRecovery) }
    );
  }, [target, upsertLiveEvent]);

  useEffect(() => {
    if (!visible || !target?.id) return undefined;

    lastStatusRef.current = target.last_status ?? null;
    socketService.subscribeToTarget(Number(target.id));

    const socket = socketService.getSocket();
    if (socket) {
      socket.on('status-update-detail', handleStatusDetail);
      socket.on('target-status-alert', handleTargetAlert);
    }

    const removeConnect = socketService.onConnect(() => {
      socketService.subscribeToTarget(Number(target.id));
    });

    return () => {
      socketService.unsubscribeFromTarget(Number(target.id));
      const s = socketService.getSocket();
      if (s) {
        s.off('status-update-detail', handleStatusDetail);
        s.off('target-status-alert', handleTargetAlert);
      }
      removeConnect();
    };
  }, [visible, target?.id, handleStatusDetail, handleTargetAlert]);

  useEffect(() => {
    if (!visible || !target?.id) return;
    lastStatusRef.current = target.last_status ?? null;
  }, [visible, target?.id, target?.last_status]);

  useEffect(() => {
    if (!visible) {
      setLiveEvents([]);
    }
  }, [visible]);

  useEffect(() => {
    if (!visible || !target?.id) return;
    setLiveEvents([]);
    lastStatusRef.current = target.last_status ?? null;
  }, [visible, target?.id]);

  const events = useMemo(() => {
    if (!target) return [];
    const merged = mergeTargetEvents(logs, target, liveEvents);
    return collapseStatusTransitions(merged);
  }, [logs, target, liveEvents]);

  if (!target) return null;

  return (
    <Card
      size="small"
      style={{ marginBottom: 16 }}
      title={(
        <Space>
          <BellOutlined />
          <span>List Alert Status Changes Latest</span>
        </Space>
      )}
    >
      {events.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          No status changes in the current log range. Only transitions (UP → DOWN → UP) are shown, not every check interval.
        </Text>
      ) : (
        <List
          size="small"
          dataSource={events.slice(0, DISPLAY_LIMIT)}
          renderItem={(item) => (
            <List.Item style={{ padding: '8px 0' }}>
              <List.Item.Meta
                avatar={getAlertStatusIcon(item.status, item.status === 'UP' && item.source === 'live')}
                title={(
                  <Space size={4} wrap>
                    <Tag color={item.status === 'UP' ? 'green' : item.status === 'DOWN' ? 'red' : 'orange'}>
                      {item.status}
                    </Tag>
                    {item.source === 'live' && (
                      <Tag icon={<WifiOutlined />} color="blue" style={{ fontSize: 10 }}>Latest</Tag>
                    )}
                  </Space>
                )}
                description={(
                  <div style={{ fontSize: 12 }}>
                    <div>Latency: {formatLatency(item.latency)} · Last seen: {toLocalTime(item.timestamp)}</div>
                    {item.errorMessage && (
                      <div style={{ color: '#cf1322', marginTop: 2 }}>{item.errorMessage}</div>
                    )}
                  </div>
                )}
              />
            </List.Item>
          )}
        />
      )}
    </Card>
  );
};

export default TargetDetailAlerts;
